import React, { useState, useEffect } from 'react';
import { firestore, auth } from '../../firebase/config';
import './ProfileEditModal.css';

const ProfileEditModal = ({ user, userProfile, onClose, onUpdate }) => {
  const [formData, setFormData] = useState({
    name: '',
    firstname: '',
    email: '',
    phone: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  // Initialiser le formulaire avec les données de l'utilisateur
  useEffect(() => {
    if (userProfile) {
      setFormData({
        name: userProfile.name || '',
        firstname: userProfile.firstname || '',
        email: user?.email || '',
        phone: userProfile.phone || ''
      });
    }
  }, [userProfile, user]);

  // Gérer les changements dans le formulaire
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Soumettre le formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      // Mettre à jour le profil dans Firestore
      await firestore
        .collection('users')
        .doc(user.uid)
        .update({
          name: formData.name,
          firstname: formData.firstname,
          phone: formData.phone,
          updatedAt: new Date()
        });

      // Mettre à jour l'email si nécessaire
      if (user.email !== formData.email) {
        await user.updateEmail(formData.email);
      }

      setSuccess(true);
      
      // Informer le composant parent de la mise à jour
      if (onUpdate) {
        onUpdate({
          ...userProfile,
          name: formData.name,
          phone: formData.phone
        });
      }
      
      // Fermer le modal après 2 secondes
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err) {
      console.error('Erreur lors de la mise à jour du profil:', err);
      
      if (err.code === 'auth/requires-recent-login') {
        setError('Pour modifier votre email, vous devez vous reconnecter. Veuillez vous déconnecter et vous reconnecter avant de réessayer.');
      } else {
        setError('Une erreur est survenue lors de la mise à jour de votre profil. Veuillez réessayer.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="profile-edit-modal">
      <div className="profile-edit-content">
        <div className="profile-edit-header">
          <h3>Modifier mes informations</h3>
          <button 
            className="close-modal" 
            onClick={onClose}
            aria-label="Fermer"
          >
            &times;
          </button>
        </div>
        
        {success && (
          <div className="success-message">
            Vos informations ont été mises à jour avec succès.
          </div>
        )}
        
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="name">Nom *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="form-control"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="firstname">Prénom *</label>
            <input
              type="text"
              id="firstname"
              name="firstname"
              value={formData.firstname}
              onChange={handleChange}
              className="form-control"
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="email">Email *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="form-control"
              required
            />
            <div className="text-muted">
              La modification de votre email peut nécessiter une nouvelle authentification.
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="phone">Téléphone *</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="form-control"
              required
            />
          </div>
          
          <div className="profile-edit-actions">
            <button
              type="button"
              className="btn btn-cancel"
              onClick={onClose}
              disabled={loading}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProfileEditModal;